// ════════════════════════════════════════════════════════════════════════════
// M2.7 — Cuestionario PEMM-Proceso (formulario · 0745, shell BPMM · 0746)
// Clon estructural de components/CuestionarioProcesoForm (BPMM): mismo acordeón
// colapsable por grupo, mismos controles "Expandir/Contraer bloques/notas", mismo
// render de pregunta (afirmación + botones + verbal + nota). Diferencias PEMM:
// grupo = categoría (5), dentro sub-cabecera por habilitador con su nivel P en vivo,
// escala 1-4, y guardado POR RESPUESTA (PATCH .../respuesta devuelve el heatmap →
// onSaved lo sube al panel → gráficas izq en vivo). Es "bare": el panel lo envuelve
// en su card + título + scroll acotado (position:absolute/inset:0), igual que el BPMM.
// ════════════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../../../i18n';
import { BotonDictado } from '../../../shared/components/BotonDictado';
import {
  PREGUNTAS_PEMM_PROCESO,
  ESCALA_PEMM,
  preguntasDeHabilitador,
} from './preguntas-pemm-proceso';
import {
  CATEGORIAS_PEMM,
  HABILITADORES_PEMM,
  NIVELES_PEMM,
  descriptorHabilitador,
  type PNivel,
  type CategoriaPEMM,
} from './descriptores-pemm-proceso';
import { guardarRespuestaPemm, type HeatmapPemm } from './api-pemm-proceso';

type Respuestas = Record<string, { valor: number; nota?: string }>;

const A1 = '#1F3864';
const A2 = '#2F5496';
const CE = '#33BCE1';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';
const VERDE = '#16A34A';
const AMBAR = '#E0A800';
const ROJO = '#DC2626';
const NIVELES: PNivel[] = ['P1', 'P2', 'P3', 'P4'];
const hexNivel = (p: PNivel) => {
  const n = NIVELES.indexOf(p) + 1;
  return n >= 3 ? VERDE : n >= 2 ? AMBAR : ROJO;
};

const miniBtn: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 5,
  border: `1px solid ${GM}`,
  background: W,
  color: A2,
  cursor: 'pointer',
  fontFamily: "'DM Sans',sans-serif",
};

/** Nivel local (redondeo de la media 1-4) para el badge en vivo del habilitador. */
function nivelLocal(habClave: string, resp: Respuestas): PNivel | null {
  const vals = preguntasDeHabilitador(habClave)
    .map((p) => resp[p.clave]?.valor)
    .filter((v): v is number => typeof v === 'number');
  if (!vals.length) return null;
  const media = vals.reduce((a, b) => a + b, 0) / vals.length;
  return NIVELES[Math.max(0, Math.min(3, Math.round(media) - 1))];
}

export function CuestionarioPemmForm({
  evaluacionId,
  respuestas,
  readOnly,
  selectedHab,
  onSelectHab,
  onSaved,
}: {
  evaluacionId: string;
  respuestas?: Respuestas | null;
  readOnly?: boolean;
  selectedHab?: string | null;
  onSelectHab?: (clave: string) => void;
  onSaved?: (heatmap: HeatmapPemm) => void;
}) {
  const { t } = useI18n();
  const k = ((t.process as Record<string, unknown>)?.madurezPemm ?? {}) as Record<string, string>;
  const tx = (key: string, fallback: string): string => k[key] ?? fallback;

  const [resp, setResp] = useState<Respuestas>(respuestas ?? {});
  const [notaOpen, setNotaOpen] = useState<Record<string, boolean>>({});
  // Acordeón por categoría: todas ABIERTAS por defecto (true = cerrada).
  const [grupoCerrado, setGrupoCerrado] = useState<Record<string, boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-sembrar el estado local al cambiar de evaluación.
  const respKey = useMemo(() => JSON.stringify(respuestas ?? {}), [respuestas]);
  useEffect(() => {
    setResp(respuestas ?? {});
    const no: Record<string, boolean> = {};
    for (const [clave, v] of Object.entries(respuestas ?? {})) if (v.nota) no[clave] = true;
    setNotaOpen(no);
    setGrupoCerrado({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluacionId, respKey]);

  const persistir = async (preguntaClave: string, valor: number, nota?: string) => {
    if (readOnly) return;
    setSavingKey(preguntaClave);
    setError(null);
    try {
      const r = await guardarRespuestaPemm(evaluacionId, {
        preguntaClave,
        valor,
        nota: nota && nota !== '' ? nota : null,
      });
      onSaved?.(r.data);
    } catch {
      setError(tx('errorGuardar', 'No se pudo guardar (¿permiso de edición?)'));
    } finally {
      setSavingKey(null);
    }
  };

  const marcar = (preguntaClave: string, valor: number) => {
    if (readOnly) return;
    const prev = resp[preguntaClave];
    setResp((s) => ({ ...s, [preguntaClave]: { valor, nota: prev?.nota } }));
    void persistir(preguntaClave, valor, prev?.nota);
  };
  const editarNota = (preguntaClave: string, nota: string) =>
    setResp((s) => ({ ...s, [preguntaClave]: { valor: s[preguntaClave]?.valor ?? 0, nota } }));
  const guardarNota = (preguntaClave: string) => {
    const r = resp[preguntaClave];
    if (!r || !r.valor) return; // el backend exige un valor 1-4
    void persistir(preguntaClave, r.valor, r.nota);
  };

  const renderPregunta = (clave: string, texto: string) => {
    const val = resp[clave]?.valor ?? 0;
    return (
      <div key={clave} style={{ padding: '6px 0', borderBottom: '1px solid #F2F2F2' }}>
        <div style={{ fontSize: 12, color: '#000', marginBottom: 4 }}>{texto}</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {ESCALA_PEMM.map((o) => {
            const sel = val === o.valor;
            return (
              <button
                key={o.valor}
                disabled={readOnly || savingKey === clave}
                title={o.etiqueta}
                onClick={() => marcar(clave, o.valor)}
                style={{
                  width: 30,
                  height: 26,
                  borderRadius: 5,
                  border: sel ? `2px solid ${A1}` : `1px solid ${GM}`,
                  background: sel ? CE : W,
                  color: sel ? A1 : GO,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: readOnly ? 'default' : 'pointer',
                  opacity: readOnly && !sel ? 0.55 : 1,
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {o.valor}
              </button>
            );
          })}
        </div>
        {val > 0 && (
          <div style={{ fontSize: 10, color: A2, marginTop: 3, fontStyle: 'italic' }}>
            {val} — {ESCALA_PEMM.find((o) => o.valor === val)?.etiqueta}
          </div>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={() => setNotaOpen((prev) => ({ ...prev, [clave]: !prev[clave] }))}
            style={{
              marginTop: 4,
              fontSize: 10,
              color: A2,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {(notaOpen[clave] ? '− ' : '+ ') + tx('anadirNota', 'Añadir nota')}
          </button>
        )}
        {notaOpen[clave] && (
          <>
            <textarea
              value={resp[clave]?.nota ?? ''}
              onChange={(e) => editarNota(clave, e.target.value)}
              onBlur={() => guardarNota(clave)}
              maxLength={1000}
              rows={2}
              readOnly={readOnly}
              style={{
                width: '100%',
                marginTop: 4,
                fontSize: 12,
                fontFamily: "'DM Sans',sans-serif",
                border: `1px solid ${GM}`,
                borderRadius: 6,
                padding: 6,
                resize: 'vertical',
                background: readOnly ? '#F7F7F7' : W,
                color: readOnly ? GO : '#000',
                boxSizing: 'border-box',
              }}
            />
            {!readOnly && (
              <BotonDictado
                value={resp[clave]?.nota ?? ''}
                onChange={(v) => editarNota(clave, v)}
                onCommit={(v) => {
                  const r = resp[clave];
                  if (r?.valor) void persistir(clave, r.valor, v);
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const preguntasDeCategoria = (cat: CategoriaPEMM) =>
    PREGUNTAS_PEMM_PROCESO.filter((p) => p.categoria === cat);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {readOnly && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: A1,
            background: '#EAF6FB',
            border: `1px solid ${CE}`,
            borderRadius: 6,
            padding: '6px 10px',
          }}
        >
          🔒 {tx('lecturaSolo', 'Evaluación aprobada · cuestionario congelado (solo lectura)')}
        </div>
      )}
      <div style={{ fontSize: 11, color: GO }}>
        {tx(
          'cuestionarioAyuda',
          'Responde cada afirmación de 1 (Nada) a 4 (Plenamente). La media de un habilitador marca su nivel P; el proceso vale su eslabón más débil. El resultado se refleja en el heatmap y el radar al instante.',
        )}
      </div>

      {/* Cabecera: expandir/contraer bloques + notas */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => setGrupoCerrado({})} style={miniBtn}>
          ▾ {tx('expandirBloques', 'Expandir bloques')}
        </button>
        <button
          type="button"
          onClick={() =>
            setGrupoCerrado(Object.fromEntries(CATEGORIAS_PEMM.map((c) => [c.categoria, true])))
          }
          style={miniBtn}
        >
          ▸ {tx('contraerBloques', 'Contraer bloques')}
        </button>
        <button
          type="button"
          onClick={() =>
            setNotaOpen(Object.fromEntries(PREGUNTAS_PEMM_PROCESO.map((p) => [p.clave, true])))
          }
          style={miniBtn}
        >
          + {tx('expandirNotas', 'Expandir notas')}
        </button>
        <button type="button" onClick={() => setNotaOpen({})} style={miniBtn}>
          − {tx('contraerNotas', 'Contraer notas')}
        </button>
      </div>

      {error && <div style={{ color: '#7B0000', fontSize: 11 }}>{error}</div>}

      {CATEGORIAS_PEMM.map((cat) => {
        const preguntas = preguntasDeCategoria(cat.categoria);
        if (!preguntas.length) return null;
        const abierta = !grupoCerrado[cat.categoria];
        const habs = HABILITADORES_PEMM.filter((h) => h.categoria === cat.categoria);
        return (
          <div key={cat.categoria}>
            <button
              type="button"
              onClick={() =>
                setGrupoCerrado((prev) => ({ ...prev, [cat.categoria]: !prev[cat.categoria] }))
              }
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#F5F8FC',
                border: `1px solid ${GM}`,
                borderRadius: 6,
                padding: '5px 8px',
                marginBottom: abierta ? 4 : 0,
                cursor: 'pointer',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 10,
                fontWeight: 800,
                color: A2,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 11, color: A1, width: 10 }}>{abierta ? '▾' : '▸'}</span>
              <span style={{ flex: 1 }}>{cat.nombre}</span>
              <span style={{ color: GO, fontWeight: 600 }}>{preguntas.length}</span>
            </button>
            {abierta &&
              habs.map((h) => {
                const nl = nivelLocal(h.clave, resp);
                const sel = selectedHab === h.clave;
                return (
                  <div key={h.clave} style={{ marginBottom: 4 }}>
                    <div
                      onClick={() => onSelectHab?.(h.clave)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        padding: '3px 2px',
                        borderRadius: 4,
                        background: sel ? '#F2F2F2' : 'transparent',
                      }}
                    >
                      <strong style={{ fontSize: 11.5, color: A1, flex: 1 }}>{h.nombre}</strong>
                      {nl && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: W,
                            background: hexNivel(nl),
                            borderRadius: 5,
                            padding: '1px 6px',
                          }}
                          title={NIVELES_PEMM.find((x) => x.nivel === nl)?.nombre}
                        >
                          {NIVELES_PEMM.find((x) => x.nivel === nl)?.etiqueta ?? nl}
                        </span>
                      )}
                    </div>
                    {preguntasDeHabilitador(h.clave).map((p) => renderPregunta(p.clave, p.texto))}
                    <details style={{ margin: '2px 0 6px' }}>
                      <summary
                        style={{ fontSize: 10, color: A2, cursor: 'pointer', fontWeight: 700 }}
                      >
                        {tx('refNiveles', 'Referencia de niveles (Hammer)')}
                      </summary>
                      <div
                        style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
                      >
                        {NIVELES.map((n) => (
                          <div key={n} style={{ fontSize: 10.5, color: GO, lineHeight: 1.35 }}>
                            <strong style={{ color: hexNivel(n) }}>
                              {NIVELES_PEMM.find((x) => x.nivel === n)?.etiqueta}
                            </strong>{' '}
                            {descriptorHabilitador(h.clave, n)}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
